import Context from "shared/util/Context";
import OkC from "shared/util/OkcApi";
import ClientStats from "shared/util/ClientStats";
import * as Sentry from "@sentry/browser";
import { QUESTION_CARD_IMPORTANCE } from "./constants";
import { isReboarding } from "apps/onboarding/util/helper";

const triggerQuestionFailureStat = () => {
	let stat = "onboarding funnel - question card submit failure - platform:";
	stat = `${stat}${process.env.PLATFORM}`;
	ClientStats.update(stat);
};

export function answerQuestion(qid, answerBool, meta = {}) {
	const answer = answerBool ? 1 : 2;

	const req = OkC.post(`/questions/${qid}`, {
		data: {
			answer,
			match_answers: [answer],
			importance: QUESTION_CARD_IMPORTANCE,
			public: 1,
			metadata: {
				onboarding: true,
				requirement: meta.requirement,
				progress: meta.progress,
			},
		},
	}).then((res) => {
		if (!res.success) {
			triggerQuestionFailureStat();
		}
	}).catch(() => {
		triggerQuestionFailureStat();
	});

	return req;
}

export function loadInitialParams() {
	return OkC.get("/onboarding/params", {
		data: {
			needs_reboarding: isReboarding(),
		},
	});
}

export function getQuestion() {
	return OkC.get("/onboarding/question");
}

export function getQuestionRequirement() {
	return OkC.get("/onboarding/question_requirement");
}

export function answerMultipartQuestion(params) {
	return OkC.post("/onboarding/question", {
		data: {
			qid: params.qid,
			answer: params.answer,
			match_answers: params.matchAnswers,
			important: params.important,
			skip: params.skip,
		},
	});
}

export function uploadEssay(essayInput, essayId) {
	return OkC.post("/profile/edit/essays", {
		data: {
			essays: {
				[essayId]: essayInput,
			},
		},
	});
}

export function getPassionsEssaysInfo() {
	return OkC.get("/passions/essaysInfo");
}

export function savePassionsEssay({ essayId, essayPics, essayContents }) {
	const data = {
		essayid: essayId,
		essay_pics: essayPics,
		contents: essayContents,
	};

	return OkC.post("/profile/save_essay", {
		data,
	});
}

export function markStartOnboarding(configName, steps) {
	return OkC.post("/onboarding/start", {
		version: 2,
		data: {
			config_name: configName,
			order: steps.reduce((prev, step) => {
				prev.push(step.statname);
				return prev;
			}, []),
		},
	});
}

/**
 * Take the data for the current step of onboarding, convert it to what the
 * public api expects, then send it to the api.
 *
 * @param {object} params - the current steps data
 * @return {promise} - the api request
 */
export function markOnboardingStepAsCompleted(params) {
	const topLevelParams = {
		name: true,
		step: true,
		index: true,
		next_step: true,
		skipped: true,
		requirement: true,
		progress: true,
		config_name: "undefined",
	};

	const stepDataParams = {
		char_count: true,
		has_photo: true,
		skipped_any: true,
	};

	const apiData = {
		step_data: {},
	};

	Object.keys(params).forEach((key) => {
		if (topLevelParams[key]) {
			apiData[key] = params[key];
		} else if (stepDataParams[key]) {
			apiData.step_data[key] = params[key];
		}
	});

	return OkC.post("onboarding/step", {
		version: 2,
		data: apiData,
	});
}

export function finishUserlog(configName, steps) {
	return OkC.post("/onboarding/finish", {
		data: {
			config_name: configName,
			order: steps.reduce((prev, step) => {
				prev.push(step.statname);
				return prev;
			}, []),
		},
	});
}

export function activateAccountAPI() {
	return OkC.get("/signup/activate");
}

export function getCompletionStatus() {
	return OkC.get("signup/completion", { api: 1 });
}

// turning off provisional is, at this point, not a pubexpress endpoint
export function turnOffProvisional() {
	// we only want to bother turning off provisional if they are provisional.
	if (!Context || !Context.get("provisionalProfile")) {
		return Promise.resolve();
	}

	return OkC.legacy("/signup", {
		type: "POST",
		data: {
			complete_profile: 1,
			okc_api: 1,
		},
	});
}

export function maybeDebugIncompleteProfile(onboardingState) {
	return getCompletionStatus()
		.then((res) => {
			if (res && res.requirements && res.requirements.length) {
				Sentry.withScope(() => {
					const { requirementProgress, activityCount, steps } = onboardingState;

					Sentry.setExtra("steps", steps);
					Sentry.setExtra("activityCount", activityCount);
					Sentry.setExtra("requirementProgress", requirementProgress);
					Sentry.setExtra("requirements", res.requirements);
					Sentry.captureMessage("Onboarding: finished with incomplete profile");
				});
			}
		})
		.catch((err) => err);
}

export default {
	uploadEssay,
	answerQuestion,
	markStartOnboarding,
	markOnboardingStepAsCompleted,
	turnOffProvisional,
};



// WEBPACK FOOTER //
// ./src/apps/onboarding/util/web.js